import {
  BarChart3,
  Bell,
  BookOpen,
  Bot,
  Building2,
  ChatCircleDots,
  Flask,
  Globe,
  GraduationCap,
  ListChecks,
  Repeat,
  ShieldCheck,
  Waveform,
  Hash,
  HelpCircle,
  Image as ImageIcon,
  Images,
  Vault,
  MessageSquare,
  MessagesSquare,
  TelegramLogo,
  User,
  UserPlus,
  Users,
  Megaphone,
  XLogo,
  type LucideIcon,
  Sparkles,
} from "@/shared/ui/icons";
import type { ReactNode } from "react";

import type { PanelId } from "./PanelRegistry";

/**
 * Mini-aperçus par panel, utilises dans le PanelPicker. Style "console
 * technique" : des rectangles vides qui evoquent la forme du contenu, sans
 * texte ni couleur (juste accent + grayscale neutre).
 *
 * Convention : chaque thumbnail rend un mini-cockpit-panel — bordure +
 * header bar + zone body avec le pattern caracteristique du panel.
 *   - Conversations : grille de cards
 *   - Vault         : grille de tiles carrees
 *   - Persona       : liste de lignes longues
 *   - Team          : table 4 lignes 4 colonnes
 *   - Telegram/X    : en-tête créateur + rail de nav vertical + contenu
 *
 * Les couleurs sont locales (gris neutre + accent mark) pour rester lisibles
 * sur le bg-elevated du picker.
 */
interface PanelVisual {
  icon: LucideIcon;
  thumbnail: ReactNode;
}

function FrameWrap({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-fg/[0.06]">
      {/* Mini header bar — fait penser au cockpit-panel-header */}
      <div className="flex h-3 shrink-0 items-center gap-0.5 bg-fg/[0.06] px-1">
        <div className="h-1 w-3 bg-fg/30" />
        <div className="h-1 w-2 bg-fg/15" />
      </div>
      <div className="min-h-0 flex-1 p-1">{children}</div>
    </div>
  );
}

function ConversationsThumb() {
  return (
    <FrameWrap>
      <div className="grid h-full grid-cols-3 gap-0.5">
        {Array.from({ length: 9 }).map((_, i) => (
          <div
            key={i}
            className="flex flex-col gap-px bg-fg/[0.08] p-0.5"
          >
            <div className="h-0.5 w-2/3 bg-fg/30" />
            <div className="h-0.5 w-full bg-fg/15" />
            {i === 0 && <div className="mt-auto h-0.5 w-1/3 bg-mark" />}
          </div>
        ))}
      </div>
    </FrameWrap>
  );
}

function VaultThumb() {
  return (
    <FrameWrap>
      <div className="grid h-full grid-cols-4 grid-rows-3 gap-0.5">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className={
              (i === 2 || i === 7 ? "bg-fg/25" : "bg-fg/[0.08]")
            }
          />
        ))}
      </div>
    </FrameWrap>
  );
}

function TeamThumb() {
  return (
    <FrameWrap>
      <div className="flex h-full flex-col">
        {/* table header */}
        <div className="flex h-1 gap-0.5 pb-0.5">
          <div className="w-1/4 bg-fg/30" />
          <div className="w-1/3 bg-fg/15" />
          <div className="w-1/6 bg-fg/15" />
          <div className="flex-1 bg-fg/15" />
        </div>
        {/* rows */}
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="flex flex-1 items-center gap-0.5 py-0.5"
          >
            <div className="h-1 w-1/4 bg-fg/15" />
            <div className="h-0.5 w-1/3 bg-fg/10" />
            <div
              className={
                "h-1 w-1/6 " + (i === 0 ? "bg-mark/60" : "bg-fg/10")
              }
            />
          </div>
        ))}
      </div>
    </FrameWrap>
  );
}

function CreatorsThumb() {
  // 4 mini-cards sur 2x2 — chacune avec un avatar, un nom et 2 mini-stat
  // boxes (fans + revenue). Des aplats d'encre : pas de couleur à soi.
  return (
    <FrameWrap>
      <div className="grid h-full grid-cols-2 grid-rows-2 gap-0.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex flex-col overflow-hidden bg-fg/[0.08]">
            <div className="flex flex-1 flex-col gap-px p-px">
              <div className="flex items-center gap-px">
                <div className="h-1 w-1 bg-fg/30" />
                <div className="h-px w-3 bg-fg/30" />
              </div>
              <div className="mt-auto flex gap-px">
                <div className="h-1 flex-1 bg-fg/[0.08]" />
                <div className="h-1 flex-1 bg-fg/[0.08]" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </FrameWrap>
  );
}

function SpacesThumb() {
  // Liste de Spaces — chaque ligne = avatar carre + 2 lignes de texte.
  // Mix d'icones (megaphone pour channel, users pour group) pour evoquer
  // la diversite de types Telegram. Une ligne porte un badge unread.
  return (
    <FrameWrap>
      <div className="flex h-full flex-col gap-0.5">
        {Array.from({ length: 4 }).map((_, i) => {
          const isChannel = i === 1;
          const isUnread = i === 0;
          return (
            <div
              key={i}
              className="flex items-center gap-0.5 py-0.5"
            >
              <div
                className={
                  "flex h-2 w-2 shrink-0 items-center justify-center " +
                  (isChannel ? "bg-mark/60" : "bg-fg/30")
                }
              >
                <div className="h-0.5 w-0.5 bg-canvas" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="h-0.5 w-3/4 bg-fg/30" />
                <div className="mt-px h-0.5 w-2/3 bg-fg/10" />
              </div>
              {isUnread && <div className="h-1 w-1 shrink-0 bg-mark" />}
            </div>
          );
        })}
      </div>
    </FrameWrap>
  );
}

function OrganisationThumb() {
  // 3 mini-cards d'orgs (logos carres + 2 chips stats) sur une colonne —
  // miroir simplifie de la grille de cards rendue par OrganisationPanel.
  return (
    <FrameWrap>
      <div className="flex h-full flex-col gap-0.5">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="flex flex-1 items-center gap-0.5 bg-fg/[0.08] p-0.5"
          >
            <div className="h-3 w-3 shrink-0 bg-fg/30" />
            <div className="flex min-w-0 flex-1 flex-col gap-px">
              <div className="h-0.5 w-2/3 bg-fg/30" />
              <div className="h-0.5 w-1/2 bg-fg/10" />
            </div>
            <div className="flex shrink-0 gap-px">
              <div className="h-1.5 w-2 bg-fg/[0.08]" />
              <div className="h-1.5 w-2 bg-fg/[0.08]" />
            </div>
          </div>
        ))}
      </div>
    </FrameWrap>
  );
}

function ChannelThumb({ navCount }: { navCount: number }) {
  // Schéma des panneaux de canal (Telegram / Twitter) : en-tête = avatar du
  // créateur (dropdown) + rangée de 3 stats ; corps = rail de nav vertical à
  // gauche (1re entrée active = accent mark) + zone de contenu. Miroir du chrome
  // `ChannelCreatorHeader` + `ChannelNavRail`. `navCount` = nb d'entrées du rail
  // (Telegram en a plus que Twitter) → distingue les deux canaux d'un coup d'œil.
  return (
    <FrameWrap>
      <div className="flex h-full flex-col gap-0.5">
        {/* En-tête : avatar créateur + 3 stats */}
        <div className="flex shrink-0 items-center gap-0.5 pb-0.5">
          <div className="h-2.5 w-2.5 shrink-0 bg-fg/30" />
          <div className="flex flex-1 gap-px">
            <div className="h-2 flex-1 bg-fg/[0.08]" />
            <div className="h-2 flex-1 bg-fg/[0.08]" />
            <div className="h-2 flex-1 bg-fg/[0.08]" />
          </div>
        </div>
        {/* Corps : rail de nav vertical + contenu */}
        <div className="flex min-h-0 flex-1 gap-0.5">
          <div className="flex w-1.5 shrink-0 flex-col gap-px">
            {Array.from({ length: navCount }).map((_, i) => (
              <div
                key={i}
                className={"h-1 w-full " + (i === 0 ? "bg-mark/70" : "bg-fg/15")}
              />
            ))}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-px">
            <div className="h-0.5 w-3/4 bg-fg/30" />
            <div className="h-0.5 w-full bg-fg/10" />
            <div className="h-0.5 w-5/6 bg-fg/10" />
            <div className="h-0.5 w-2/3 bg-fg/10" />
          </div>
        </div>
      </div>
    </FrameWrap>
  );
}

function DocumentationThumb() {
  // Schéma de la doc : barre d'onglets (une pastille active accent) + corps
  // avec un gros titre et des lignes de texte + une carte d'exemple.
  return (
    <FrameWrap>
      <div className="flex h-full flex-col gap-0.5">
        {/* Barre d'onglets */}
        <div className="flex shrink-0 gap-0.5 pb-0.5">
          <div className="h-1.5 w-3 bg-mark/70" />
          <div className="h-1.5 w-2 bg-fg/15" />
          <div className="h-1.5 w-2 bg-fg/15" />
          <div className="h-1.5 w-2 bg-fg/15" />
        </div>
        {/* Corps : titre + lignes + carte d'exemple */}
        <div className="flex min-h-0 flex-1 flex-col gap-px">
          <div className="h-1 w-1/2 bg-fg/30" />
          <div className="h-0.5 w-full bg-fg/10" />
          <div className="h-0.5 w-5/6 bg-fg/10" />
          <div className="mt-auto h-2.5 w-full bg-fg/[0.08]" />
        </div>
      </div>
    </FrameWrap>
  );
}

function FormationThumb() {
  // Schéma de la formation : une colonne de vidéos — un titre au-dessus de
  // chaque cadre 16:9, et un triangle « lecture » au centre du premier.
  return (
    <FrameWrap>
      <div className="flex h-full flex-col gap-0.5">
        <div className="h-1 w-1/2 bg-fg/30" />
        <div className="relative aspect-video w-full bg-fg/[0.08]">
          <div className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 bg-mark/70" />
        </div>
        <div className="mt-auto h-1 w-1/3 bg-fg/30" />
        <div className="h-1.5 w-full bg-fg/[0.08]" />
      </div>
    </FrameWrap>
  );
}

function FallbackThumb() {
  return (
    <FrameWrap>
      <div className="flex h-full items-center justify-center">
        <div className="h-2 w-2 bg-fg/30" />
      </div>
    </FrameWrap>
  );
}

/**
 * Map panelId → { icon, thumbnail }. Un panel non liste tombe sur le
 * fallback (icone HelpCircle + thumb gris).
 */
export const PANEL_VISUALS: Partial<Record<PanelId, PanelVisual>> = {
  conversations: { icon: MessagesSquare, thumbnail: <ConversationsThumb /> },
  vault: { icon: Vault, thumbnail: <VaultThumb /> },
  team: { icon: Users, thumbnail: <TeamThumb /> },
  creators: { icon: UserPlus, thumbnail: <CreatorsThumb /> },
  spaces: { icon: Megaphone, thumbnail: <SpacesThumb /> },
  organisation: { icon: Building2, thumbnail: <OrganisationThumb /> },
  documentation: { icon: BookOpen, thumbnail: <DocumentationThumb /> },
  formation: { icon: GraduationCap, thumbnail: <FormationThumb /> },
  // Personnel = son espace perso : l'icône du rail du kiosque (celle de
  // l'aperçu que Kayla a préférée, 07-10-2026).
  personnel: { icon: Users, thumbnail: <FallbackThumb /> },
  analytique: { icon: BarChart3, thumbnail: <FallbackThumb /> },
  // RishFans : l'unique onglet du kiosque d'un partenaire — sans icône, son
  // rail montrerait un point d'interrogation.
  rishfans: { icon: Megaphone, thumbnail: <FallbackThumb /> },
  // Panneaux de canal — icône de la plateforme + schéma du chrome de canal
  // (en-tête créateur + rail de nav). navCount reflète le nb d'entrées du rail.
  telegram: { icon: TelegramLogo, thumbnail: <ChannelThumb navCount={6} /> },
  twitter: { icon: XLogo, thumbnail: <ChannelThumb navCount={2} /> },
  // Les écrans du Bureau (rail gauche, cf `ecransDuBureau.ts`) : chacun a son
  // icône, sinon le rail montre un point d'interrogation.
  cyrano: { icon: Bot, thumbnail: <FallbackThumb /> },
  lab: { icon: Flask, thumbnail: <FallbackThumb /> },
  herald: { icon: Bell, thumbnail: <FallbackThumb /> },
  // Les onglets d'un hôte (Telegram, Créatrices, Organisation) — leurs
  // icônes servent aussi à l'onglet d'un workspace persisté qui les porte.
  security: { icon: ShieldCheck, thumbnail: <FallbackThumb /> },
  sites: { icon: Globe, thumbnail: <FallbackThumb /> },
  audio: { icon: Waveform, thumbnail: <FallbackThumb /> },
  "vault-review": { icon: ListChecks, thumbnail: <FallbackThumb /> },
  "sans-reponse": { icon: ChatCircleDots, thumbnail: <FallbackThumb /> },
  relances: { icon: Repeat, thumbnail: <FallbackThumb /> },
  // organisation-detail : pas de thumbnail (panel hidden du picker) mais on
  // declare l'icone pour que le TabStrip generique ait un fallback quand
  // l'org n'a pas de logoUrl. Le rendu specialise OrganisationTabContent
  // remplace l'icone par le logo de l'org quand il est dispo.
  "organisation-detail": { icon: Building2, thumbnail: <FallbackThumb /> },
  // Panels caches (ouverts par click dans un parent) — pas dans le picker,
  // mais on declare l'icone pour qu'ils aient le meme traitement visuel
  // dans le TabStrip que les autres onglets. Cf TabStrip.tsx : le rendu
  // generique lit `getPanelVisual(panelId).icon`. Pour `fan` et
  // `creator-detail`, l'avatar/photo prend la place de l'icone (cf
  // FanTabContent / CreatorTabContent), donc on n'a pas besoin d'entry ici.
  "vault-item": { icon: ImageIcon, thumbnail: <FallbackThumb /> },
  "space-detail": { icon: Hash, thumbnail: <FallbackThumb /> },
  "fan-chat": { icon: MessageSquare, thumbnail: <FallbackThumb /> },
  "fan-info": { icon: User, thumbnail: <FallbackThumb /> },
  francis: { icon: Sparkles, thumbnail: <FallbackThumb /> },
  "fan-stats": { icon: BarChart3, thumbnail: <FallbackThumb /> },
  "fan-media": { icon: Images, thumbnail: <FallbackThumb /> },
};

export function getPanelVisual(panelId: PanelId): PanelVisual {
  return PANEL_VISUALS[panelId] ?? { icon: HelpCircle, thumbnail: <FallbackThumb /> };
}
